import { useCallback } from 'react';
import { supabase, SUPABASE_URL } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';
import { AI_FN_SUFFIX } from '@/src/lib/aiFunctions';

export type ChatChunk =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; name: string; id: string }
  | { type: 'tool_result'; name: string; ok: boolean; pending?: boolean }
  | { type: 'suggestions'; items: { label: string; prompt: string }[] }
  | { type: 'done'; stop_reason?: string }
  | { type: 'error'; message: string }
  | { type: 'fallback'; reason: string };

export interface SendArgs {
  message: string;
  drawerId: string;
  venueName: string;
  history?: { role: 'user' | 'assistant'; content: string }[];
  /** Saved chat this message belongs to, so past-chat search can skip it. */
  conversationId?: string | null;
  onChunk: (chunk: ChatChunk) => void;
}

export function useChat() {
  const send = useCallback(async (args: SendArgs) => {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session) {
      args.onChunk({ type: 'error', message: 'Not signed in.' });
      return;
    }

    // Never show canned replies: if the AI can't be reached, say so honestly.
    const fallbackToStub = (reason: string) => {
      log.warn('useChat: request failed', { reason });
      const message = reason === 'network'
        ? "Couldn't reach OpsAlo just now. Check your connection and try again."
        : reason === 'anthropic_not_configured'
          ? 'The assistant is not configured for this venue yet.'
          : `Something went wrong on our side (${reason}). Please try again.`;
      args.onChunk({ type: 'error', message });
    };

    let response: Response;
    try {
      response = await fetch(`${SUPABASE_URL}/functions/v1/chat${AI_FN_SUFFIX}?forceFunctionRegion=ap-southeast-2`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: args.message,
          drawerId: args.drawerId,
          history: args.history,
          conversationId: args.conversationId ?? null,
        }),
      });
    } catch (err) {
      log.warn('useChat: network error, falling back to stub', {
        code: err instanceof Error ? err.message : 'unknown',
      });
      fallbackToStub('network');
      return;
    }

    if (response.status === 503) {
      fallbackToStub('anthropic_not_configured');
      return;
    }
    if (!response.ok) {
      fallbackToStub(`http_${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      fallbackToStub('no_body');
      return;
    }
    const decoder = new TextDecoder();
    let buffer = '';
    let finished = false; // saw a done or error chunk

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const json = line.slice(5).trim();
          if (!json) continue;
          try {
            const chunk = JSON.parse(json) as ChatChunk;
            if (chunk.type === 'done' || chunk.type === 'error') finished = true;
            args.onChunk(chunk);
          } catch {
            /* skip malformed sse line */
          }
        }
      }
    } catch (err) {
      finished = true;
      args.onChunk({
        type: 'error',
        message: err instanceof Error ? err.message : 'stream interrupted',
      });
    } finally {
      reader.releaseLock();
    }
    if (!finished) {
      args.onChunk({ type: 'error', message: 'The reply was cut off before it finished. Please try again.' });
    }
  }, []);

  return { send };
}
